// Vendor portal (migration 054) on the vendor panel: prices the vendor sent in the portal, waiting for HHL to accept or
// decline, and catalogs they sent for Claude to read into draft products (Their products). Each is the app's usual table
// (DataTable: views, columns, resizing, Copy / CSV).
import { useState } from "react";
import type { Item, Vendor, VendorItem, VendorPrice } from "../../../shared/db.gen.ts";
import { breaksText, cleanBreaks, termsOf } from "../../../shared/portal.ts";
import { ago, dayText, qty } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, Input } from "../ui/controls.tsx";
import { CellControls, type Column, DataTable, type TableSection } from "../ui/DataTable.tsx";
import { Popover } from "../ui/Popover.tsx";
import { Section } from "../ui/RecordPanel.tsx";
import { toast } from "../ui/toast.tsx";

type Terms = ReturnType<typeof termsOf>;
type PriceRow = VendorPrice & { item: Item | undefined; now: Terms | null; next: Terms; by: string; order: number };

const was = (x: string) => <span className="text-muted line-through">{x || "none"}</span>;
/** Waiting: what the link has now → what they ask; decided: what they asked. */
const change = (r: PriceRow, now: string, next: string, differs: boolean) =>
  r.status === "waiting" && differs ? (
    <span className="truncate">
      {was(now)} → <span className="font-medium">{next}</span>
    </span>
  ) : (
    <span className="truncate">{next}</span>
  );

const BANDS: Record<string, TableSection> = {
  waiting: { key: "waiting", label: "To decide", order: 0 },
  decided: { key: "decided", label: "Decided", order: 1 },
};

/**
 * Prices the vendor sent, waiting for HHL: what the link has now and what they ask, to accept (it goes onto the link,
 * so onto new POs) or decline with a word back they see. The last few decided show under them.
 */
export function VendorPrices({ vendor }: { vendor: Vendor }) {
  const prices = useRows("vendor_price").filter((p) => p.vendor_id === vendor.id);
  const links = useRows("vendor_item");
  const items = useRows("item");
  const contacts = useRows("vendor_contact");
  // Table views --- the View and Columns menus, by the section's words
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  if (!prices.length) return null;
  const waiting = prices.filter((p) => p.status === "waiting").sort((a, b) => a.created_at.localeCompare(b.created_at));
  const decided = prices
    .filter((p) => p.status === "accepted" || p.status === "declined")
    .sort((a, b) => String(b.decided_at).localeCompare(String(a.decided_at)))
    .slice(0, 5);
  const rows: PriceRow[] = [...waiting, ...decided].map((p, order) => {
    const link: VendorItem | undefined = links.find((l) => l.id === p.vendor_item_id);
    return {
      ...p,
      item: link ? items.find((i) => i.id === link.item_id) : undefined,
      now: link ? termsOf(link) : null,
      next: { currency: p.currency, price_breaks: cleanBreaks(p.price_breaks), moq: p.moq == null ? null : Number(p.moq), lead_time_days: p.lead_time_days },
      by: contacts.find((c) => c.id === p.created_by)?.name ?? "",
      order,
    };
  });
  const columns: Column<PriceRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(110px,1fr)",
      value: (r) => r.item?.sku ?? "?",
      cell: (r) => <span className="truncate font-mono font-medium">{r.item?.sku ?? "?"}</span>,
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(120px,1fr)",
      value: (r) => r.item?.name ?? "",
      cell: (r) => <span className="truncate text-muted">{r.item?.name}</span>,
    },
    {
      key: "price",
      header: "Price",
      width: "minmax(150px,1.4fr)",
      value: (r) => breaksText(r.next),
      text: (r) => (r.status === "waiting" ? `${r.now ? breaksText(r.now) : ""} → ${breaksText(r.next)}` : breaksText(r.next)),
      cell: (r) => change(r, r.now ? breaksText(r.now) : "", breaksText(r.next), true),
    },
    {
      key: "moq",
      header: "MOQ",
      width: "110px",
      value: (r) => r.next.moq ?? "",
      cell: (r) => r.next.moq != null && change(r, r.now?.moq == null ? "" : qty(r.now.moq), qty(r.next.moq), r.next.moq !== r.now?.moq),
    },
    {
      key: "lead",
      header: "Lead time",
      width: "120px",
      value: (r) => r.next.lead_time_days ?? "",
      cell: (r) =>
        r.next.lead_time_days != null &&
        change(
          r,
          r.now?.lead_time_days == null ? "" : `${r.now.lead_time_days} days`,
          `${r.next.lead_time_days} days`,
          r.next.lead_time_days !== r.now?.lead_time_days,
        ),
    },
    {
      key: "effective_from",
      header: "From",
      headerTitle: "The day they say it starts (accepting applies it now)",
      width: "90px",
      value: (r) => r.effective_from ?? "",
      cell: (r) =>
        r.effective_from && (
          <span className="text-muted" title="Accepting applies it now">
            {dayText(r.effective_from)}
          </span>
        ),
    },
    {
      key: "note",
      header: "Their note",
      width: "minmax(120px,1fr)",
      cell: (r) =>
        r.note && (
          <span className="truncate" title={r.note}>
            “{r.note}”
          </span>
        ),
    },
    {
      key: "reply",
      header: "Our reply",
      width: "minmax(110px,1fr)",
      cell: (r) =>
        r.reply && (
          <span className="truncate text-muted" title={r.reply}>
            “{r.reply}”
          </span>
        ),
    },
    { key: "by", header: "By", width: "100px", optional: true, cell: (r) => <span className="truncate text-faint">{r.by}</span> },
    {
      key: "when",
      header: "When",
      headerTitle: "Sent (to decide), or decided",
      width: "90px",
      // To decide: the oldest first; decided: the latest first
      value: (r) => r.order,
      text: (r) => (r.status === "waiting" ? ago(r.created_at) : r.decided_at ? dayText(r.decided_at) : ""),
      cell: (r) => <span className="text-faint">{r.status === "waiting" ? ago(r.created_at) : r.decided_at ? dayText(r.decided_at) : ""}</span>,
    },
    {
      key: "decision",
      header: "",
      width: "170px",
      value: () => null,
      cell: (r) =>
        r.status === "waiting" ? (
          can("vendor_price.accept") && <Decide price={r} />
        ) : (
          <Badge tone={r.status === "accepted" ? "ok" : "neutral"}>{r.status === "accepted" ? "Accepted" : "Declined"}</Badge>
        ),
    },
  ];
  return (
    <Section title="Prices from them" count={waiting.length}>
      <div className="flex items-start gap-2 px-2 pb-2">
        <p className="min-w-0 flex-1 text-muted">
          {waiting.length > 0 && <Badge tone="warn">{waiting.length} to decide</Badge>} Sent by {vendor.short_name || vendor.name} in the portal. Accept one and
          it goes onto their link to the part, so onto new POs; decline it with a word they'll see.
        </p>
        <div ref={setSlot} className="flex shrink-0 items-center gap-1" />
      </div>
      <div className="px-2 pb-2 text-[12px]">
        <DataTable
          fit
          rows={rows}
          columns={columns}
          sectionOf={(r) => (r.status === "waiting" ? BANDS.waiting : BANDS.decided)}
          defaultSort={{ key: "when" }}
          exportName="Prices from them"
          viewsSlot={slot}
          pageFilters={false}
          rowNoun={["price", "prices"]}
        />
      </div>
    </Section>
  );
}

/** Accept their price, or decline it with a word back they'll see (in a popover by the button). */
function Decide({ price: p }: { price: PriceRow }) {
  const [reply, setReply] = useState("");
  return (
    <CellControls className="gap-1">
      <Button
        size="sm"
        variant="primary"
        onClick={async () => {
          if (await mutate("vendor_price.accept", { id: p.id })) toast.ok(`${p.item?.sku ?? "The part"}: their price is on the link now`);
        }}
      >
        Accept
      </Button>
      <Popover
        width={320}
        button={(toggle) => (
          <Button size="sm" onClick={toggle}>
            Decline…
          </Button>
        )}
      >
        {(close) => (
          <div className="flex gap-1 text-[12px]">
            <Input
              autoFocus
              value={reply}
              maxLength={1000}
              placeholder="A word back they'll see (optional)"
              onChange={(e) => setReply(e.target.value)}
              className="h-7 min-w-0 flex-1 text-[12px]"
            />
            <Button
              size="sm"
              onClick={async () => {
                if (await mutate("vendor_price.decline", { id: p.id, reply: reply.trim() })) close();
              }}
            >
              Decline
            </Button>
            <Button size="sm" variant="ghost" onClick={close}>
              Cancel
            </Button>
          </div>
        )}
      </Popover>
    </CellControls>
  );
}

const TONE = { new: "warn", reading: "accent", done: "ok", failed: "danger" } as const;
const STATUS = { new: "To read", reading: "Being read", done: "Done", failed: "Couldn't read" } as const;

type UploadRow = ReturnType<typeof useUploads>[number];
function useUploads(vendorId: string) {
  const uploads = useRows("portal_upload");
  const contacts = useRows("vendor_contact");
  const products = useRows("vendor_product");
  return uploads
    .filter((u) => u.vendor_id === vendorId)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((u) => ({
      ...u,
      from: contacts.find((c) => c.id === u.contact_id)?.name || "Them",
      drafts: products.filter((p) => p.upload_id === u.id).length,
    }));
}

const uploadColumns: Column<UploadRow>[] = [
  {
    key: "created_at",
    header: "Sent",
    width: "90px",
    text: (u) => ago(u.created_at),
    cell: (u) => <span className="text-muted">{ago(u.created_at)}</span>,
  },
  {
    key: "status",
    header: "Status",
    width: "110px",
    value: (u) => STATUS[u.status as keyof typeof STATUS] ?? u.status,
    cell: (u) => <Badge tone={TONE[u.status as keyof typeof TONE] ?? "neutral"}>{STATUS[u.status as keyof typeof STATUS] ?? u.status}</Badge>,
  },
  { key: "from", header: "From", width: "110px", cell: (u) => <span className="truncate text-muted">{u.from}</span> },
  {
    key: "files",
    header: "Files",
    width: "minmax(140px,1.4fr)",
    value: (u) => u.files.map((_, i) => u.file_names[i] || `File ${i + 1}`).join(", "),
    cell: (u) => (
      <span className="flex min-w-0 gap-x-3 truncate">
        {u.files.map((f, i) => (
          <a key={f} href={`/files/${f}`} target="_blank" rel="noreferrer" className="shrink-0 text-accent-text hover:underline">
            {u.file_names[i] || `File ${i + 1}`}
          </a>
        ))}
      </span>
    ),
  },
  {
    key: "note",
    header: "Their note",
    width: "minmax(120px,1fr)",
    cell: (u) =>
      u.note && (
        <span className="truncate" title={u.note}>
          “{u.note}”
        </span>
      ),
  },
  {
    key: "result",
    header: "What came of it",
    width: "minmax(120px,1fr)",
    cell: (u) => (
      <span className="truncate text-muted" title={u.result}>
        {u.result}
      </span>
    ),
  },
  {
    key: "drafts",
    header: "Drafts",
    width: "80px",
    align: "right",
    cell: (u) => (u.drafts > 0 ? <span className="text-faint">{u.drafts} drafts</span> : null),
  },
];

/**
 * Catalogs the vendor sent (any form: PDF, Excel, photos) with their note and what came of reading them. Claude reads
 * them over the MCP (vendor_uploads, vendor_upload_file, vendor_drafts, vendor_upload_handle) into draft products.
 */
export function VendorUploads({ vendor }: { vendor: Vendor }) {
  const uploads = useUploads(vendor.id);
  // Table views --- the View and Columns menus, by the section's words
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  if (!uploads.length) return null;
  const todo = uploads.filter((u) => u.status === "new" || u.status === "reading").length;
  return (
    <Section title="Catalogs they sent" count={uploads.length}>
      <div className="flex items-start gap-2 px-2 pb-2">
        <p className="min-w-0 flex-1 text-muted">
          {todo > 0 && <Badge tone="warn">{todo} to read</Badge>} Ask Claude to read them (the HHL Ops MCP's vendor_uploads): it makes draft products under
          Their products, matched by their SKU, and says here what came of it.
        </p>
        <div ref={setSlot} className="flex shrink-0 items-center gap-1" />
      </div>
      <div className="px-2 pb-2 text-[12px]">
        <DataTable
          fit
          rows={uploads}
          columns={uploadColumns}
          defaultSort={{ key: "created_at", desc: true }}
          exportName="Catalogs they sent"
          viewsSlot={slot}
          pageFilters={false}
          rowNoun={["catalog", "catalogs"]}
        />
      </div>
    </Section>
  );
}
